import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast, Toaster } from "sonner";
import Footer from "@/components/Footer";
import { Loader2, LockKeyhole, Sun, Moon, Globe } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const { branding, theme, setTheme } = useBranding();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(username, password);
      toast.success(`${t("welcome")}, ${user.first_name}`);
      navigate(user.role === "kiosk" ? "/kiosk" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      <Toaster position="top-right" richColors />
      <div className="flex-1 grid lg:grid-cols-2">
        {/* Hero */}
        <div
          className="hidden lg:flex flex-col justify-between p-12 relative"
          style={{
            backgroundImage:
              "linear-gradient(135deg, rgba(29,78,216,0.92) 0%, rgba(30,64,175,0.94) 100%), url('https://images.pexels.com/photos/9574344/pexels-photo-9574344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="flex items-center gap-3 text-white">
            {branding?.logo_b64 ? (
              <img src={branding.logo_b64} alt="logo" className="h-10 w-10 rounded-md bg-white/10 object-contain p-1" />
            ) : (
              <div className="h-10 w-10 rounded-md bg-white/15 text-white grid place-items-center font-bold backdrop-blur">Rx</div>
            )}
            <span className="font-heading font-bold text-lg">{branding?.system_name || "Apotheken Portal"}</span>
          </div>
          <div className="text-white">
            <div className="overline text-white/80 mb-4">Mitarbeiter-Portal</div>
            <h1 className="font-heading text-5xl font-extrabold leading-[1.05] tracking-tight mb-4">
              Das komplette<br />Apotheken-Betriebssystem.
            </h1>
            <p className="text-white/85 text-lg max-w-md leading-relaxed">
              Zeiterfassung, Dienstpläne, Übergabebuch, Botendienst und Team-Chat — alles an einem Ort.
              Multi-Standort. DSGVO-konform. Made in Düsseldorf.
            </p>
          </div>
          <div className="text-white/60 text-xs">© {new Date().getFullYear()} • DSGVO-konform</div>
        </div>

        {/* Form */}
        <div className="flex flex-col p-6 sm:p-12 justify-center">
          <div className="absolute top-6 right-6 flex gap-2">
            <Button data-testid="login-lang-btn" variant="ghost" size="sm" onClick={() => setLang(lang === "de" ? "en" : "de")}>
              <Globe className="w-4 h-4 mr-1.5" />
              <span className="text-xs uppercase font-bold">{lang}</span>
            </Button>
            <Button data-testid="login-theme-btn" variant="ghost" size="icon" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>

          <div className="w-full max-w-md mx-auto">
            <div className="mb-8 lg:hidden flex items-center gap-3">
              {branding?.logo_b64 ? (
                <img src={branding.logo_b64} alt="logo" className="h-10 w-10 rounded-md object-contain p-1 bg-slate-100 dark:bg-slate-800 border border-border/40" />
              ) : (
                <div className="h-10 w-10 rounded-md bg-blue-700 text-white grid place-items-center font-bold">Rx</div>
              )}
              <span className="font-heading font-bold text-lg">{branding?.system_name || "Apotheken Portal"}</span>
            </div>
            <div className="overline mb-3">{t("login")}</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">
              Willkommen zurück.
            </h2>
            <p className="text-slate-500 mb-8 text-sm">
              Melde dich mit deinem Mitarbeiter-Account an.
            </p>
            <Card className="border-border/60 shadow-sm">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-wider">{t("username")}</Label>
                    <Input
                      id="username"
                      data-testid="login-username-input"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wider">{t("password")}</Label>
                    <Input
                      id="password"
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="mt-1.5"
                    />
                  </div>
                  <Button
                    data-testid="login-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-full bg-blue-700 hover:bg-blue-800 text-white h-11 font-semibold"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LockKeyhole className="w-4 h-4 mr-2" />}
                    {t("login")}
                  </Button>
                </form>
                <div className="mt-6 pt-6 border-t border-border/60 text-xs text-slate-500">
                  <strong>Kiosk-Modus</strong> · Öffne <a href="/kiosk" className="text-blue-700 font-semibold hover:underline">/kiosk</a> auf dem iPad-Terminal.
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
